import type { ReactNode } from "react";
import { text, toggle } from "@web/theme/theme";

export type ToggleProps = {
  label: ReactNode;
  description?: ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  /** Render only the switch; `label` (a string) becomes its aria-label. For row trailings. */
  hideLabel?: boolean;
  className?: string;
};

/** Switch row: label on the left, 48×28 switch on the right (≥44px target). With `hideLabel`, just the switch. */
export function Toggle({ label, description, checked, onChange, disabled, hideLabel = false, className = "" }: ToggleProps) {
  const track = (
    <span aria-hidden className={`${toggle.track} ${checked ? toggle.on : toggle.off}`}>
      <span className={`${toggle.knob} ${checked ? toggle.knobOn : ""}`} />
    </span>
  );
  if (hideLabel) {
    return (
      <button type="button" role="switch" aria-checked={checked} aria-label={typeof label === "string" ? label : undefined} disabled={disabled} onClick={() => onChange(!checked)} className={`${toggle.bare} ${disabled ? "opacity-50" : ""} ${className}`.trim()}>
        {track}
      </button>
    );
  }
  return (
    <button type="button" role="switch" aria-checked={checked} disabled={disabled} onClick={() => onChange(!checked)} className={`${toggle.row} ${disabled ? "opacity-50" : ""} ${className}`.trim()}>
      <span className="flex-1 min-w-0 flex flex-col">
        <span className={`${text.variant.body} ${text.tone.default}`}>{label}</span>
        {description && <span className={`${text.variant.caption} ${text.tone.tertiary}`}>{description}</span>}
      </span>
      {track}
    </button>
  );
}
